import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, browserSessionPersistence, type Auth } from 'firebase/auth'
import { getFirestore, type Firestore } from 'firebase/firestore'

// Firebase client instance (singleton pattern)
let firebaseApp: FirebaseApp | null = null
let authInstance: Auth | null = null
let firestoreInstance: Firestore | null = null

/**
 * Get or initialize Firebase app (client-side)
 * Uses environment variables from window.ENV
 */
export function getFirebaseApp(): FirebaseApp {
	if (firebaseApp) {
		return firebaseApp
	}

	// Get config from window.ENV (set in root.tsx)
	const env = typeof window !== 'undefined' ? (window as any).ENV : {}

	const firebaseConfig = {
		apiKey: env.FIREBASE_API_KEY,
		authDomain: env.FIREBASE_AUTH_DOMAIN,
		projectId: env.FIREBASE_PROJECT_ID,
		storageBucket: env.FIREBASE_STORAGE_BUCKET,
		messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID,
		appId: env.FIREBASE_APP_ID,
	}

	// Validate required config
	if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
		throw new Error('Firebase configuration is incomplete')
	}

	firebaseApp = initializeApp(firebaseConfig)
	return firebaseApp
}

/**
 * Get Firebase Auth instance (client-side)
 * Configured with browserSessionPersistence for SSR compatibility
 */
export function getFirebaseAuth(): Auth {
	if (authInstance) {
		return authInstance
	}

	const app = getFirebaseApp()
	authInstance = getAuth(app)

	// CRITICAL for SSR with redirect flow: Use session persistence
	// - Persists auth state only for the current browser session (tab)
	// - Survives page reloads (needed for getRedirectResult to work)
	// - Cleared when tab is closed
	// - Does NOT persist across browser restarts (unlike localStorage)
	authInstance.setPersistence(browserSessionPersistence).catch((error) => {
		console.error('Failed to set auth persistence:', error)
	})

	return authInstance
}

/**
 * Get Firestore instance (client-side, respects security rules)
 */
export function getFirebaseFirestore(): Firestore {
	if (firestoreInstance) {
		return firestoreInstance
	}

	const app = getFirebaseApp()
	firestoreInstance = getFirestore(app)
	return firestoreInstance
}
